import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './lib/auth.jsx'
import {useSessionGeneration} from './lib/useSessionGeneration.js'
import { NAV } from './nav.js'
import { INTERNAL_ROUTE, INTERNAL_LEGACY_ROUTE } from './lib/surfaces.js'
import AppShell from './components/AppShell.jsx'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import FullScreenLoader from './components/FullScreenLoader.jsx'

import Login from './pages/Login.jsx'
import NativeConnectionContinue from './pages/NativeConnectionContinue.jsx'
import ToolAccess from './pages/ToolAccess.jsx'
import Home from './pages/Home.jsx'
import Workspace from './pages/Workspace.jsx'
import CommandCenter from './pages/CommandCenter.jsx'
import CustomerJourney from './pages/CustomerJourney.jsx'
import Reviews from './pages/Reviews.jsx'
import PricingEngine from './pages/PricingEngine.jsx'
import ArtworkStudio from './pages/ArtworkStudio.jsx'
import ProductBuilders from './pages/ProductBuilders.jsx'
import Presentations from './pages/Presentations.jsx'
import TeamRooms from './pages/TeamRooms.jsx'
import TopTierTeams from './pages/TopTierTeams.jsx'
import AiTools from './pages/AiTools.jsx'
import TamCoverage from './pages/TamCoverage.jsx'
import BowlEligibility from './pages/BowlEligibility.jsx'
import TradeDesk from './pages/TradeDesk.jsx'
import TamBasis from './pages/TamBasis.jsx'
import DataIntegrity from './pages/DataIntegrity.jsx'
import Reconciliation from './pages/Reconciliation.jsx'
import GoverningBodies from './pages/GoverningBodies.jsx'
import BodyCoverage from './pages/BodyCoverage.jsx'
import InvisibleRecords from './pages/InvisibleRecords.jsx'
import SchoolDirectory from './pages/SchoolDirectory.jsx'
import TeamManagement from './pages/TeamManagement.jsx'
import MyWorklist from './pages/MyWorklist.jsx'
import NotFound from './pages/NotFound.jsx'

export default function App() {
  const { isLoading } = useAuth()
  const sessionGeneration = useSessionGeneration()

  // While we validate a stored token, hold the paper splash so we don't
  // flash the login screen at an already-authenticated user.
  if (isLoading) return <FullScreenLoader />

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell key={sessionGeneration} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Workspace />} />
        <Route path="workspace" element={<Workspace />} />
        <Route path="performance" element={<Workspace initialView="performance" />} />
        <Route path="platform-overview" element={<Home />} />
        <Route path="command-center" element={<CommandCenter />} />
        {/* THE TRADE DESK. The board is CC_VIEW and the page gates itself on
            it; the backend re-decides every write, so a rep who follows a
            shared link is told what they may do rather than shown a blank
            screen. The executive move is cc_rep_assign server-side whether or
            not this route renders its button. */}
        <Route path="trade-desk" element={<TradeDesk />} />
        {/* PRODUCTION — renamed from "Customer Journey" 2026-09-11 on Brian's
            call, because it is the INTERNAL manufacturing view and the 14-stage
            tracker inside it already called itself production.

            ⚠️ THE OLD PATH REDIRECTS AND MUST KEEP DOING SO. Bookmarks, pasted
            links and prior dispatches all point at /customer-journey. A rename
            that 404s the old URL gets judged on that first click, before anyone
            sees the screen that works. `replace` so it does not trap the Back
            button. Both paths come from lib/surfaces.js, so a future rename
            cannot leave the redirect pointing at a route that no longer
            exists. */}
        <Route path={INTERNAL_ROUTE.slice(1)} element={<CustomerJourney />} />
        <Route
          path={INTERNAL_LEGACY_ROUTE.slice(1)}
          element={<Navigate to={INTERNAL_ROUTE} replace />}
        />
        {/* Its own place in the platform, not a tab inside the money. */}
        <Route path="reviews" element={<Reviews />} />
        <Route path="pricing" element={<PricingEngine />} />
        <Route path="artwork-studio" element={<ArtworkStudio />} />
        <Route path="product-builders" element={<ProductBuilders />} />
        <Route path="presentations" element={<Presentations />} />
        <Route path="team-rooms" element={<TeamRooms />} />
        <Route path="top-tier-teams" element={<TopTierTeams />} />
        {/* Launcher into SR's Custom GPTs. A door, never a copy — a GPT
            runs on ChatGPT with the user's own sign-in and cannot be
            embedded. See lib/aiTools.js. */}
        <Route path="ai-tools" element={<AiTools />} />
        {/* Exec-only. The page gates itself on can(TEAM_MANAGE) and every
            route behind it is re-checked server-side — typing the URL as a rep
            gets a denial notice here and a 403 from the API. */}
        {/* exec + ops. Gates itself on can(SYSTEM_RUN); every /api/coaches/
            route is mapped to SYSTEM_RUN in the backend's permissions.js, and
            anything unmapped is denied — so the gate here is only what gets
            drawn. */}
        <Route path="tam" element={<TamCoverage />} />
        {/* 🔴 MANAGER-ONLY, AND NOT BY THIS LINE. `/api/tam/basis` is
            money.read / money.write server-side — exec-only in POLICY, a
            second isExec lock inside each handler, and the path on
            MONEY_SURFACES so no machine identity reaches it. Drawing the route
            for everyone and letting the server refuse is deliberate: the page
            renders the server's own 403 sentence, so a rep who follows a
            shared link is told it is manager-only rather than shown a blank
            screen that reads like a bug. */}
        <Route path="tam-basis" element={<TamBasis />} />
        <Route path="championship-integrity" element={<DataIntegrity />} />
        {/* THE ONE SCREEN THAT ANSWERS "IS THE UNIVERSE CLEAN?".
            Its own route rather than a tab on Championship Integrity, which
            answers a different and narrower question: that page checks each
            record for structural defects it can see from the records alone.
            This one counts the things that are invisible BY CONSTRUCTION —
            a body with no real date can never trip a clock, so it is absent
            from the SLA denominator rather than failing it, and a record
            with no governing body is absent from every per-body total
            rather than appearing in one as wrong. A defect that cannot
            appear on an existing screen does not get a tab on it. */}
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="governing-bodies" element={<GoverningBodies />} />
        {/* The reconciliation the board itself cannot do. Its own route rather
            than a sixth tab: every tab on that page is driven by a registry
            array, and this page's whole subject is the bodies that have no
            entry in one. */}
        <Route path="body-coverage" element={<BodyCoverage />} />
        {/* THE FOUR SETS THAT WERE FILES ON ONE LAPTOP.
          *
          * ⚠️ TWO ROUTES FOR ONE PAGE, AND BOTH ARE LOAD-BEARING. The bare
          * `/invisible-records` is what the sidebar links, so the nav entry
          * cannot 404; `/invisible-records/:surface` is what each tab links, so
          * a single surface can be sent to somebody in a message. The page
          * falls back to the first surface when `:surface` is not one it knows,
          * rather than rendering nothing — a typed URL landing on a blank page
          * is the silent zero in its most confusing form. */}
        <Route path="invisible-records" element={<InvisibleRecords />} />
        <Route path="invisible-records/:surface" element={<InvisibleRecords />} />
        <Route path="school-directory" element={<SchoolDirectory />} />
        <Route path="team" element={<TeamManagement />} />
        <Route path="tool-access" element={<ToolAccess />} />
        <Route path="native-connection/continue" element={<NativeConnectionContinue />} />
        {/* ⚠️ ITS OWN URL, DELIBERATELY NOT THE INDEX ROUTE. RepWorklist was
            built, asserted and verified against the live books, then sat
            unreachable from main.jsx — so `check-clickable`, which walks the
            import graph from main.jsx and examines only files that ship, had
            never once looked at it. Routing it is what puts it under the gate.
            It becomes the landing only after the pilot. */}
        <Route path="my-worklist" element={<MyWorklist />} />
        {/* Bowl eligibility. A read of one weekly board, so it is one route and
            no sub-routes: there is no per-team record on this page to deep-link
            to, and inventing one is how a dead control ships. */}
        <Route path="bowl-eligibility" element={<BowlEligibility />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

// Re-export for convenience in tests / tooling.
export { NAV }
export const AppNavFallback = () => <Navigate to="/" replace />
